import { useRef } from 'react';
import classNames from 'classnames';

import { type SliderSlide } from '@jsmdg/yoshi';
import { LazySlider } from '../LazySlider';
import { trackSwitchMotif } from './helpers';
import { useSwiperSync } from './useSwiperSync';
import { mainSliderConfig } from './valueVoucherSliderConfig';
import styles from './BestsellerValueVoucherSlider.module.scss';

type MainVoucherSliderProps = {
    readonly slides: SliderSlide[];
    readonly activeSlideIndex: number;
    readonly onSlideChange: (index: number) => void;
    readonly a11yNextSlide: string;
    readonly a11yPreviousSlide: string;
};

const MainVoucherSlider = ({
    a11yNextSlide,
    a11yPreviousSlide,
    activeSlideIndex,
    onSlideChange,
    slides,
}: MainVoucherSliderProps): JSX.Element | null => {
    const containerRef = useRef<HTMLDivElement>(null);
    const slideKeys = slides.map(slide => slide.key).join(',');
    const { isSyncingRef, onSwiperSlideChange } = useSwiperSync(containerRef, {
        activeSlideIndex,
        forceCenteredSlides: true,
        onSlideChange,
        slideKeys,
        slidesCount: slides.length,
    });

    const handleCarouselSlide = async (direction: string): Promise<void> => {
        if (isSyncingRef.current) {
            return;
        }

        await trackSwitchMotif('slide in carousel', direction);
    };

    if (!slides.length) {
        return null;
    }

    return (
        <div
            ref={containerRef}
            className={classNames('position-relative w-100', styles.mainSliderSwiper)}
        >
            <LazySlider
                a11yNextSlide={a11yNextSlide}
                a11yPreviousSlide={a11yPreviousSlide}
                classes={{
                    controlsContainer: classNames(
                        'position-absolute w-100 m-0 pe-none',
                        styles.mainSliderControls,
                    ),
                }}
                config={mainSliderConfig}
                handleShowNext={async () => handleCarouselSlide('slide next')}
                handleShowPrevious={async () => handleCarouselSlide('slide previous')}
                isFreeModeEnabled={false}
                loop
                onSlideChange={onSwiperSlideChange}
                slides={slides}
            />
        </div>
    );
};

export { MainVoucherSlider };
